import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  UserIcon,
  ShareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { adminSocialAPI } from '../../services/adminApi';
import LoadingSpinner from '../LoadingSpinner';
import StatusBadge from '../common/StatusBadge';

const UserConnectionsModal = ({ userId, userEmail, isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserConnections();
    }
  }, [isOpen, userId]);

  const loadUserConnections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminSocialAPI.getAllConnections({
        user_id: userId,
        limit: 100 // Get all connections for this user
      });
      
      setConnections(response.data.connections || []);
    } catch (err) {
      console.error('Error loading user connections:', err);
      setError('Failed to load user connections');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async (connectionId) => {
    try {
      setRefreshing(true);
      await adminSocialAPI.forceTokenRefresh(connectionId);
      
      // Reload connections to show updated status
      await loadUserConnections();
    } catch (err) {
      console.error('Error refreshing token:', err);
      setError('Failed to refresh token');
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: '📘',
      instagram: '📷',
      twitter: '🐦',
      linkedin: '💼',
      youtube: '📺',
      tiktok: '🎵',
      threads: '🧵'
    };
    return icons[platform] || '🌐';
  };

  const getHealthStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'warning':
      case 'rate_limited':
        return <ClockIcon className="w-4 h-4 text-yellow-500" />;
      case 'expired':
      case 'invalid':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="w-4 h-4 text-gray-400" />;
    }
  };

  const getConnectionStats = () => {
    const total = connections.length;
    const healthy = connections.filter(c => c.token_health_status === 'healthy').length;
    const warning = connections.filter(c => ['warning', 'rate_limited'].includes(c.token_health_status)).length;
    const error = connections.filter(c => ['expired', 'invalid'].includes(c.token_health_status)).length;
    
    return { total, healthy, warning, error };
  };

  const stats = getConnectionStats();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-admin-100 rounded-full flex items-center justify-center mr-4">
                      <UserIcon className="w-5 h-5 text-admin-600" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                        User Connections
                      </Dialog.Title>
                      <p className="text-sm text-gray-600">{userEmail}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={onClose}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-admin-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <ShareIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                    <p className="text-sm text-blue-600">Total</p>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-900">{stats.healthy}</p>
                    <p className="text-sm text-green-600">Healthy</p>
                  </div>
                  
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <ClockIcon className="w-5 h-5 text-yellow-600" />
                    </div>
                    <p className="text-2xl font-bold text-yellow-900">{stats.warning}</p>
                    <p className="text-sm text-yellow-600">Warning</p>
                  </div>
                  
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-2xl font-bold text-red-900">{stats.error}</p>
                    <p className="text-sm text-red-600">Errors</p>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {/* Connections List */}
                <div className="bg-gray-50 rounded-lg max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="flex justify-center items-center py-12">
                      <LoadingSpinner />
                      <p className="ml-4 text-sm text-gray-500">Loading connections...</p>
                    </div>
                  ) : connections.length === 0 ? (
                    <div className="text-center py-12">
                      <ShareIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No connections found for this user</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {connections.map((connection) => (
                        <div key={connection.connection_id} className="p-4 hover:bg-gray-100 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center flex-1">
                              {/* Platform Icon */}
                              <div className="flex-shrink-0 mr-4">
                                <span className="text-2xl">{getPlatformIcon(connection.platform)}</span>
                              </div>
                              
                              {/* Connection Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center">
                                  <h4 className="text-sm font-medium text-gray-900 capitalize mr-2">
                                    {connection.platform}
                                  </h4>
                                  <StatusBadge status={connection.connection_status} size="small" />
                                </div>
                                <p className="text-sm text-gray-600 truncate">
                                  {connection.account_name}
                                </p>
                                <p className="text-xs text-gray-400">
                                  ID: {connection.account_id}
                                </p>
                              </div>

                              {/* Health Status */}
                              <div className="flex items-center mr-4">
                                {getHealthStatusIcon(connection.token_health_status)}
                                <span className="ml-2 text-sm text-gray-600 capitalize">
                                  {connection.token_health_status.replace('_', ' ')}
                                </span>
                              </div>

                              {/* Timestamps */}
                              <div className="text-right text-xs text-gray-500 mr-4">
                                <div>Created: {formatDate(connection.created_at)}</div>
                                <div>Last Sync: {formatDate(connection.last_synced)}</div>
                                {connection.last_refreshed && (
                                  <div>Last Refresh: {formatDate(connection.last_refreshed)}</div>
                                )}
                              </div>

                              {/* Actions */}
                              <div className="flex-shrink-0">
                                {(connection.token_health_status === 'expired' || 
                                  connection.token_health_status === 'warning') && (
                                  <button
                                    onClick={() => handleRefreshToken(connection.connection_id)}
                                    disabled={refreshing}
                                    className="flex items-center px-3 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <ArrowPathIcon className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                                    Refresh Token
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Additional Details (Expandable) */}
                          {connection.connection_metadata && Object.keys(connection.connection_metadata).length > 0 && (
                            <div className="mt-3 pl-12">
                              <details className="text-xs">
                                <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                                  Additional Details
                                </summary>
                                <div className="mt-2 p-2 bg-white rounded border">
                                  <pre className="text-xs text-gray-600 overflow-x-auto">
                                    {JSON.stringify(connection.connection_metadata, null, 2)}
                                  </pre>
                                </div>
                              </details>
                            </div>
                          )}

                          {/* Permissions */}
                          {connection.permissions && connection.permissions.length > 0 && (
                            <div className="mt-2 pl-12">
                              <div className="flex flex-wrap gap-1">
                                {connection.permissions.map((permission, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                  >
                                    {permission}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    User ID: {userId}
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={loadUserConnections}
                      disabled={loading}
                      className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowPathIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                    
                    <button
                      onClick={onClose}
                      className="px-4 py-2 bg-admin-600 text-white rounded-md text-sm font-medium hover:bg-admin-700"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UserConnectionsModal;